import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Badge, Checkbox, Label, Loading, Select } from '@rematter/ui-web';

import { useUpdateServiceTypeTaskDefinitionMutation } from '~/modules/transportation/hooks/mutations/useUpdateServiceTypeTaskDefinitionMutation';
import { useServiceTypeTaskDefinitionsQuery } from '~/modules/transportation/hooks/queries/useServiceTypeTaskDefinitionsQuery';
import { ServiceTypeTaskDefinitionRecord } from '~/modules/transportation/types';

interface TaskDefinitionEditorProps {
  serviceTypeId: string;
}

export const TaskDefinitionEditor = ({
  serviceTypeId,
}: TaskDefinitionEditorProps) => {
  const t = useTranslations();

  const { data: taskDefinitionsData, isLoading } =
    useServiceTypeTaskDefinitionsQuery({
      filter: { serviceTypeId },
    });

  const { mutate: updateTaskDefinition } =
    useUpdateServiceTypeTaskDefinitionMutation();

  const taskDefinitions = React.useMemo(
    () => taskDefinitionsData?.taskDefinitions ?? [],
    [taskDefinitionsData?.taskDefinitions],
  );

  const assetRoleOptions = React.useMemo(
    () => [
      {
        key: 'inbound',
        label: t.serviceTypes.dialog.taskDefinitions.assetRoles.inbound,
      },
      {
        key: 'outbound',
        label: t.serviceTypes.dialog.taskDefinitions.assetRoles.outbound,
      },
      {
        key: 'none',
        label: t.serviceTypes.dialog.taskDefinitions.assetRoles.none,
      },
    ],
    [t],
  );

  const getLocationLabel = (locationRef: string | undefined) => {
    if (!locationRef) return null;
    const locations = t.serviceTypes.dialog.taskDefinitions.locations;
    return locations[locationRef as keyof typeof locations] ?? locationRef;
  };

  const renderRow = (def: ServiceTypeTaskDefinitionRecord) => {
    const locationLabel = getLocationLabel(def.locationRef);

    return (
      <div
        key={def.id}
        className="bg-background flex items-center gap-3 rounded-md border border-neutral/20 px-3 py-2 text-sm"
      >
        <span className="w-8 font-medium text-neutral/60">{def.sequence}.</span>
        <span className="font-medium">
          {t.serviceTypes.dialog.taskDefinitions.taskTypes[
            def.taskType as keyof typeof t.serviceTypes.dialog.taskDefinitions.taskTypes
          ] ?? def.taskType}
        </span>
        {locationLabel && (
          <Badge color="neutral" size="small">
            {locationLabel}
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-3">
          <Select
            label={t.serviceTypes.dialog.taskDefinitions.assetRole}
            value={def.assetRole ?? 'none'}
            onChange={(key) => {
              updateTaskDefinition({
                id: def.id,
                assetRole: key === 'none' || !key ? null : key,
              });
            }}
            options={assetRoleOptions}
          />
          <Checkbox
            isSelected={def.allowLocationOverride}
            onChange={(checked) => {
              updateTaskDefinition({
                id: def.id,
                allowLocationOverride: checked,
              });
            }}
          >
            {t.serviceTypes.dialog.taskDefinitions.allowLocationOverride}
          </Checkbox>
        </div>
      </div>
    );
  };

  return (
    <div className="@TaskDefinitionEditor mt-4">
      <div className="mb-2">
        <Label>{t.serviceTypes.dialog.taskDefinitions.title}</Label>
      </div>
      <Loading isLoading={isLoading}>
        {taskDefinitions.length > 0 ? (
          <div className="space-y-2">{taskDefinitions.map(renderRow)}</div>
        ) : (
          <div className="text-sm text-neutral/50">
            {t.serviceTypes.dialog.taskDefinitions.empty}
          </div>
        )}
      </Loading>
    </div>
  );
};
